import { Box3, type Object3D, type Scene, Vector3 } from "three";
import { computeProductBoundingBox } from "./productViewerCameraFit";
import { getSceneRevision, markProductSceneChanged } from "./sceneActivity";
import {
  footprintRadiusFromBox,
  publishProductBounds,
  publishProductFootprintRadius,
} from "./studioFloorExtent";

/** Scratch for the one `Box3.translate` below, so no frame allocates. */
const OFFSET_SHIFT = new Vector3();

export function createFloorAlignmentTracker() {
  return {
    /** Scene revision the product was last seated on the floor at. */
    revision: -1,
    /**
     * Scene revision `box` was measured at. Ahead of `revision` while a drag
     * holds the re-seat back, so drag frames at an unchanged revision reuse
     * the box instead of traversing the product again.
     */
    measuredRevision: -1,
    group: null as Object3D | null,
    floorY: Number.NaN,
    box: new Box3(),
  };
}

export function updateProductFloorAlignment(
  scene: Scene,
  group: Object3D,
  floorY: number,
  tracker: ReturnType<typeof createFloorAlignmentTracker>,
  /**
   * Whether the product may be re-seated on the floor this frame. False
   * during a drag: the measurement still happens, so the floor keeps
   * following the product, but moving the product itself under the pointer
   * would fight the gesture.
   */
  align = true,
): boolean {
  const revision = getSceneRevision(scene);
  const sameTarget = tracker.group === group && tracker.floorY === floorY;
  if (sameTarget && tracker.revision === revision) return false;

  if (!sameTarget || tracker.measuredRevision !== revision) {
    tracker.group = group;
    tracker.floorY = floorY;
    tracker.measuredRevision = -1;
    // A new group or floor has not been seated yet, whatever the revision.
    if (!sameTarget) tracker.revision = -1;
    group.updateMatrixWorld(true);
    computeProductBoundingBox(group, tracker.box);
    if (tracker.box.isEmpty()) {
      // No revision is recorded, so the next frame traverses again. An empty
      // box means no part has committed its geometry yet — the leases land
      // in `useLayoutEffect` — and recording the revision here would make
      // re-alignment depend on some later caller happening to bump it. The
      // component this was extracted from retried every frame until the box
      // was non-empty, and a product that never gets aligned sinks through
      // the floor in whatever the frame after this one draws.
      //
      // The bounds are published as empty even so. They are what the shadow
      // caster proxy is fitted to, and a stale non-empty box from the product
      // that was just replaced makes a drag cast the *previous* product's
      // silhouette — `shouldUseShadowProxy` only asks whether bounds exist.
      // The footprint radius deliberately keeps its last value: the floor
      // patch holding its size for a frame is invisible, where shrinking it to
      // nothing is a visible flash.
      publishProductBounds(scene, tracker.box);
      return false;
    }
    tracker.measuredRevision = revision;
    // The ground layers size themselves off this box. Published here rather
    // than measured again there: this is the one place the product is
    // already traversed on every revision, and both have to agree on the
    // footprint or the floor patch and the product disagree about where the
    // case stands.
    //
    // Deliberately before the `align` bail-out. A drag is exactly when the
    // footprint is changing under the user's hand, so it is the moment the
    // floor most needs to follow; gating this on the same flag left the patch
    // frozen until `endDrag`, which clears 100 ms after the mouse comes up.
    publishProductFootprintRadius(scene, footprintRadiusFromBox(tracker.box));
    // Same measurement, read by the shadow caster proxy.
    publishProductBounds(scene, tracker.box);
  }

  // `revision` stays behind `measuredRevision`, so the frame the drag ends
  // re-seats the product from the cached box without measuring again.
  if (!align) return false;

  const offset = floorY - tracker.box.min.y;
  if (Math.abs(offset) > 0.0001) {
    group.position.y += offset;
    // The box was published above, before this shift, so it describes where
    // the product stood rather than where it now stands. Carried along and
    // republished: the shadow caster proxy is fitted to these bounds from a
    // later `useFrame` of the same frame, and would otherwise seat its box
    // `offset` off the product for the frame an alignment happens in.
    tracker.box.translate(OFFSET_SHIFT.set(0, offset, 0));
    publishProductBounds(scene, tracker.box);
    markProductSceneChanged(group);
  }
  tracker.revision = getSceneRevision(scene);
  return true;
}
